/**
 * Command type + source builders for the CMD+K command palette.
 *
 * Phase 4 of the Lean & Clean refactor: every feature that used to be
 * "hidden behind the Mehr… accordion" or "locked inside a patient tab"
 * becomes directly reachable via the command palette. The palette is
 * the escape hatch that makes the Lean sidebar defensible — nothing
 * ever truly goes away, it just moves to a different primary surface.
 *
 * Sources:
 *   - Routes        — every nav-registered route the current user can
 *                     access, including those demoted to `hidden` via
 *                     per-org `nav_overrides`.
 *   - Patient tabs  — every tab in the patient-detail registry for the
 *                     currently-opened patient, including `outcome`
 *                     which PR 1.3 demoted to `visibility: 'hidden'`.
 *   - Actions       — top-level verbs like "Neuer Ernährungsplan" or
 *                     "GKV-Abrechnung öffnen" whose target page does
 *                     not correspond to a single URL.
 *
 * All builders are pure data — no React, no Supabase. The React
 * component in `src/components/CommandPalette.tsx` assembles them and
 * hands the resulting flat list to cmdk for fuzzy filtering.
 */

import type { LucideIcon } from 'lucide-react';
import {
  Bot,
  ClipboardList,
  FileText,
  FolderOpen,
  LineChart,
  Receipt,
  UtensilsCrossed,
  Users,
} from 'lucide-react';

import { requestOpenNutriAssistant } from '@/components/nutri-assistant/openAssistant';

import type { RouteModule, Role } from '@/features/types';
import type { Feature, SettingType } from '@/hooks/useFeatures.constants';
import { resolveRouteVisibility, type NavOverrides } from '@/features/visibility';
import {
  PATIENT_TABS,
  type PatientTabModule,
} from '@/features/patient-tab-registry';

// ─── Command shape ──────────────────────────────────────────────────────

export type CommandGroup =
  | 'Patienten'
  | 'Navigation'
  | 'Patientenakte'
  | 'Aktionen'
  | 'Administration';

export interface Command {
  /** Stable identifier — used as React key + cmdk value. */
  id: string;
  /** Primary label shown in the palette. */
  label: string;
  /** Optional subtitle / breadcrumb-style hint ("Patientenakte · Beratung"). */
  hint?: string;
  /** Logical group for the palette section. */
  group: CommandGroup;
  /** Icon shown to the left of the label. */
  icon: LucideIcon;
  /**
   * Extra searchable keywords — joined into the cmdk value so a search
   * for "bls" or "abrechnung" reaches the right command even if those
   * words are not in the label.
   */
  keywords?: string[];
  /**
   * Mutually exclusive with `onRun`. Set for commands that navigate
   * somewhere; the component wires `useNavigate().push(href)`.
   */
  href?: string;
  /**
   * Mutually exclusive with `href`. Set for commands that do not map
   * to a single URL (e.g. "Neuer Ernährungsplan" opens a modal).
   */
  onRun?: () => void;
}

// ─── Route commands ─────────────────────────────────────────────────────

export interface RouteCommandContext {
  role: Role | undefined;
  isSuperadmin: boolean;
  hasFeature: (key: Feature) => boolean;
  navOverrides: NavOverrides | null;
}

/**
 * Build a command per nav-registered route the user can access.
 *
 * Unlike the sidebar renderer, the palette does NOT filter by
 * `resolveRouteVisibility` — that's the whole point: this surface
 * reveals the routes that were demoted to `secondary`/`hidden`. The
 * role + feature gates are still respected (a user without the
 * `telemedizin` feature should not see it pop up in their palette).
 *
 * The `hint` field carries the resolved visibility so the user
 * understands why they didn't see a route in the sidebar — e.g.
 * "Unter Mehr…" or "Versteckt".
 */
export function buildRouteCommands(
  routes: readonly RouteModule[],
  ctx: RouteCommandContext,
): Command[] {
  const out: Command[] = [];
  for (const route of routes) {
    if (!route.nav) continue;
    // Role + superadmin + feature gates — same rules as the sidebar.
    if (route.nav.superadminOnly && !ctx.isSuperadmin) continue;
    if (route.feature && !ctx.hasFeature(route.feature)) continue;
    if (route.roles && route.roles.length > 0) {
      if (!ctx.role || !route.roles.includes(ctx.role)) continue;
    }

    const visibility = resolveRouteVisibility(route, ctx.navOverrides);
    const hint =
      route.nav.section === 'admin'
        ? 'Administration'
        : visibility === 'primary'
          ? 'Haupt-Sidebar'
          : visibility === 'secondary'
            ? 'Unter „Mehr…"'
            : 'Versteckt';

    out.push({
      id: `route:${route.id}`,
      label: route.nav.label,
      hint,
      group: route.nav.section === 'admin' ? 'Administration' : 'Navigation',
      icon: route.nav.icon,
      href: route.path,
      keywords: [route.id, route.path],
    });
  }
  return out;
}

// ─── Patient-tab commands ───────────────────────────────────────────────

export interface PatientTabCommandContext {
  hasFeature: (key: Feature) => boolean;
  isInpatient: boolean;
  settingType: SettingType;
  /**
   * When `true`, the scope filter is bypassed so support staff can reach
   * every tab from the command palette regardless of org setting-type.
   * Optional; defaults to `false`.
   */
  isSuperadmin?: boolean;
}

/**
 * Build a command per patient-detail tab for the currently-open patient.
 *
 * Passes through every tab, including those marked `visibility: 'hidden'`
 * (PR 1.3: `outcome`) — that's the whole point of Phase 4. The
 * feature + inpatient gates are respected so an org that never
 * purchased the klinik module doesn't see its tabs in the palette.
 */
export function buildPatientTabCommands(
  patientId: string | null,
  ctx: PatientTabCommandContext,
): Command[] {
  if (!patientId) return [];

  const isKlinikOrg = ctx.settingType === 'klinik';
  const isSuperadmin = ctx.isSuperadmin ?? false;
  return PATIENT_TABS.filter((tab) => {
    if (tab.isEnabled) {
      if (!tab.isEnabled({
        hasFeature: ctx.hasFeature,
        isInpatient: ctx.isInpatient,
        settingType: ctx.settingType,
      })) return false;
    } else if (tab.feature && !ctx.hasFeature(tab.feature)) {
      return false;
    }
    if (tab.inpatientOnly && !isKlinikOrg && !ctx.isInpatient) return false;
    if (
      !isSuperadmin &&
      tab.scope &&
      tab.scope !== 'both' &&
      tab.scope !== ctx.settingType
    ) {
      return false;
    }
    return true;
  }).map<Command>((tab: PatientTabModule) => ({
    id: `patient-tab:${tab.id}`,
    label: tab.label,
    hint: tab.visibility === 'hidden'
      ? 'Patientenakte · Versteckt'
      : 'Patientenakte',
    group: 'Patientenakte',
    icon: FolderOpen,
    href: `/patients/${patientId}?tab=${tab.id}`,
    keywords: [tab.id, 'patientenakte'],
  }));
}

// ─── Action commands ────────────────────────────────────────────────────

export interface ActionCommandContext {
  hasFeature: (key: Feature) => boolean;
  currentPatientId: string | null;
}

/**
 * Imperative verbs — "Neuer Ernährungsplan", "GKV-Abrechnung öffnen",
 * "BLS-Analyse öffnen", etc. Each targets a specific screen with an
 * intent already set (URL query, opening modal, …).
 *
 * Only actions whose backing feature is enabled are emitted; this is
 * what prevents "Neuer Ernährungsplan" from appearing for an org that
 * does not have `ernaehrungsplaene` ticked.
 */
export function buildActionCommands(ctx: ActionCommandContext): Command[] {
  const out: Command[] = [];

  // Chat-First: der KI-Assistent ist die primäre Bedienoberfläche und
  // steht deshalb immer ganz oben in den Aktionen (kein Feature-Gate —
  // der Assistent selbst ist auf jeder Seite gemountet).
  out.push({
    id: 'action:ask-nutri',
    label: 'Nutri fragen — KI-Assistent',
    hint: 'Aktion · Strg+J',
    group: 'Aktionen',
    icon: Bot,
    onRun: requestOpenNutriAssistant,
    keywords: ['ki', 'chat', 'assistent', 'nutri', 'frage', 'hilfe', 'sprache'],
  });

  if (ctx.hasFeature('ernaehrungsplaene')) {
    out.push({
      id: 'action:new-nutrition-plan',
      label: 'Neuer Ernährungsplan',
      hint: 'Aktion',
      group: 'Aktionen',
      icon: UtensilsCrossed,
      href: '/nutrition-plans?new=1',
      keywords: ['plan', 'diät', 'ernährung'],
    });
  }

  if (ctx.hasFeature('rechnungen')) {
    out.push({
      id: 'action:open-invoices',
      label: 'Rechnungen öffnen',
      hint: 'Aktion',
      group: 'Aktionen',
      icon: Receipt,
      href: '/invoices',
      keywords: ['rechnung', 'billing'],
    });
  }

  if (ctx.hasFeature('gkv_abrechnung')) {
    out.push({
      id: 'action:open-gkv-abrechnung',
      label: 'GKV-Abrechnung öffnen',
      hint: 'Aktion',
      group: 'Aktionen',
      icon: Receipt,
      // No dedicated route yet — GKV export lives inside the invoices
      // page, so we land there with an intent query the page can pick up.
      href: '/invoices?tab=gkv',
      keywords: ['gkv', 'kasse', 'abrechnung'],
    });
  }

  // Patient-context actions — only useful when a specific patient is
  // currently on screen, because they deep-link into that patient's
  // detail view.
  if (ctx.currentPatientId) {
    if (ctx.hasFeature('bls_ernaehrungsanalyse')) {
      out.push({
        id: 'action:open-bls-analyse',
        label: 'BLS-Analyse öffnen',
        hint: 'Aktion · Aktueller Patient',
        group: 'Aktionen',
        icon: LineChart,
        href: `/patients/${ctx.currentPatientId}?tab=analyse`,
        keywords: ['bls', 'nährwerte', 'analyse'],
      });
    }
    out.push({
      id: 'action:open-gncp-for-patient',
      label: 'GNCP für aktuellen Patienten',
      hint: 'Aktion · Aktueller Patient',
      group: 'Aktionen',
      icon: ClipboardList,
      href: `/patients/${ctx.currentPatientId}?tab=beratung`,
      keywords: ['gncp', 'dokumentation', 'beratung'],
    });
    out.push({
      id: 'action:open-documents-for-patient',
      label: 'Dateien des Patienten öffnen',
      hint: 'Aktion · Aktueller Patient',
      group: 'Aktionen',
      icon: FileText,
      href: `/patients/${ctx.currentPatientId}?tab=documents`,
      keywords: ['dokumente', 'dateien', 'befund'],
    });
  }

  return out;
}

// ─── Patient commands ───────────────────────────────────────────────────

export interface PatientRow {
  id: string;
  first_name: string | null;
  last_name: string | null;
}

/**
 * Wrap a list of patient rows (from a Supabase query) into the common
 * Command shape so the palette can merge them with the other sources.
 */
export function buildPatientCommands(
  patients: readonly PatientRow[],
): Command[] {
  return patients.map<Command>((p) => {
    const fullName =
      [p.first_name, p.last_name].filter(Boolean).join(' ') || 'Patient';
    return {
      id: `patient:${p.id}`,
      label: fullName,
      hint: 'Patientenakte öffnen',
      group: 'Patienten',
      icon: Users,
      href: `/patients/${p.id}`,
      // Keywords help cmdk match on partial names or first-name-only.
      keywords: [p.first_name ?? '', p.last_name ?? '', p.id],
    };
  });
}

// ─── Helpers ───────────────────────────────────────────────────────────

/**
 * Build the searchable cmdk `value` for a command. cmdk's built-in
 * fuzzy filter matches against the `value` string, so we concatenate
 * every keyword + the hint so a search for "beratung" still finds
 * the "Patientenakte · Beratung" command.
 */
export function toCmdkValue(command: Command): string {
  return [command.label, command.hint, ...(command.keywords ?? [])]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

/**
 * Parse a patient id out of a `/patients/:id` pathname. Returns `null`
 * for any other path so the palette's action-command builder knows
 * when no patient is in scope.
 */
export function extractCurrentPatientId(pathname: string): string | null {
  // `/patients/abc-123` or `/patients/abc-123?tab=beratung`
  const match = pathname.match(/^\/patients\/([^/?#]+)(?:[/?#]|$)/);
  if (!match) return null;
  // The patient list is `/patients` (no id). Don't treat that as a
  // patient page; the match above handles it via the required character.
  return match[1];
}
